/**
 * ManagerRegisterContainer
 * --
 */
import React, { useEffect, useState } from 'react';
import ManagerRegisterPresenter from './ManagerRegisterPresenter';
import { useCreateAdmin } from 'hooks/AdminHooks';
import { useDuplicateCheck } from 'hooks/AuthHooks';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { LoadingPage } from 'components';
import validateCheck from 'utils/Validation';
import MessageAlert from 'utils';
import { useUserStack } from 'store';

const ManagerRegisterContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { defaultShop = null } = props;

  /* ===== Mutate ===== */
  // 중복검사
  const { duplicate } = useDuplicateCheck();
  const userInfo = useUserStack();
  const { data: ShopInformations } = useGetShopInformations();
  const { createAdmin } = useCreateAdmin();
  const [registrationData, setRegistrationData] = useState({});
  /* ===== STATE ===== */
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 아이디 중복검사 여부
  // NOTE: DEFAULT: 중복검사 미실행 / FAIL: 중복됨 / SUCCESS: 중복안됨
  const [isChecked, setIsChecked] = React.useState('DEFAULT');

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, callback) => {
    if (v === '') {
      MessageAlert.error('잘못된 입력입니다.');
    }
    let newData = registrationData;

    newData[t] = v;
    const d = {
      ...registrationData,
    };

    setRegistrationData(d);

    callback && callback();
  };

  /**
   * admin create 함수
   * --
   */
  const handleSubmit = async (t, s = 'CREATE') => {
    const type = t.toLocaleUpperCase();
    const submit = s.toLocaleUpperCase();

    const submitType = {
      ADMIN: '관리자',
      CREATE: '생성',
    };

    const data = handleValidateCheck(type);
    if (!data) return;

    const submitFunc = {
      ADMIN: {
        CREATE: createAdmin,
      },
    };
    try {
      setIsLoading(true);

      let allSuccess = true;

      const { status } = await submitFunc[type][submit]({
        ...data,
      });

      if (status !== 200) {
        allSuccess = false;
      }

      if (allSuccess) {
        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`,
        );
        setRegistrationData({ admin_privilege: 'XXXX' });
      } else {
        MessageAlert.warning(
          `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`,
        );
      }
    } catch (err) {
      MessageAlert.error(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`,
      );
      console.error(`[CREATE ${type}] ERROR : `, err);
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 유효성 검사
   * --
   * @param {string} t
   * @returns
   */
  const handleValidateCheck = (t) => {
    // 유효성 검사 타입이 ADMIN인지 확인
    if (t === 'ADMIN') {
      const {
        type, // 관리자 타입
        admin_level, // 관리자 등급
        shop_information_id, // 매장/쇼핑몰
        admin_privilege, // 권한 등급
        admin_name, // 관리자 명
        admin_account, // 관리자 아이디
        admin_position, // 직책
        admin_email_prefix, // 이메일 앞부분
        admin_email_domain, // 이메일 도메인
        admin_phone, // 휴대폰 번호
      } = registrationData;

      // 필수 입력값 검사
      if (!defaultShop && !type) {
        MessageAlert.warning('관리자 타입을 선택해주세요.');
        return false;
      }
      if (!admin_level) {
        MessageAlert.warning('관리자 등급을 선택해주세요.');
        return false;
      }
      if (!admin_privilege || admin_privilege === 'XXXX') {
        MessageAlert.warning('올바른 권한 등급을 설정해주세요.');
        return false;
      }
      if (!admin_name) {
        MessageAlert.warning('관리자 이름을 입력해주세요.');
        return false;
      }
      if (!admin_account) {
        MessageAlert.warning('관리자 아이디를 입력해주세요.');
        return false;
      }
      // 관리자 아이디 중복검사 여부 체크
      if (isChecked === 'DEFAULT') {
        MessageAlert.warning('아이디 중복검사를 진행해주세요.');
        return false;
      }
      if (isChecked === 'FAIL') {
        MessageAlert.warning('사용할 수 없는 아이디입니다.');
        return false;
      }
      if (!admin_position) {
        MessageAlert.warning('관리자 직책을 입력해주세요.');
        return false;
      }
      if (!admin_email_prefix) {
        MessageAlert.warning('이메일 주소를 입력해주세요.');
        return false;
      }
      if (!admin_email_domain) {
        MessageAlert.warning('이메일 도메인을 입력해주세요.');
        return false;
      }
      if (
        !validateCheck('email', `${admin_email_prefix}@${admin_email_domain}`)
      ) {
        MessageAlert.warning('이메일 주소를 정확히 입력해주세요.');
        return false;
      }
      if (!admin_phone || admin_phone.length < 9) {
        MessageAlert.warning('휴대폰 번호를 올바르게 입력해주세요.');
        return false;
      }

      // 모든 유효성 검사 통과 시, 유효한 데이터 반환
      return {
        ...registrationData,
        shop_information_id: type === 'ADMIN' ? 1 : shop_information_id,
        admin_email: `${admin_email_prefix}@${admin_email_domain}`, // 이메일 합치기
        password: '1234',
      };
    } else {
      MessageAlert.warning('올바르지 않은 유효성 검사 타입입니다.');
      return false;
    }
  };

  /**
   * 아이디 중복검사
   * --
   */
  const handleDuplication = async () => {
    const { admin_account } = registrationData;
    if (isChecked === 'SUCCESS') {
      MessageAlert.warning('중복검사를 완료하셨습니다.');
      return false;
    }
    // 관리자 아이디 입력여부 체크
    if (!admin_account) {
      MessageAlert.warning('관리자 아이디를 입력해주세요.');
      return false;
    }

    try {
      // 아이디 중복검사
      const { data, status } = await duplicate({
        admin_account: admin_account,
        type: 'ADMIN',
      });

      if (status !== 200) {
        MessageAlert.warning(
          '아이디 중복검사를 진행하는데 실패하였습니다. 관리자에게 문의해주세요.',
        );
        return;
      }

      if (data) {
        MessageAlert.success('사용가능한 아이디입니다.');
        setIsChecked('SUCCESS');
      } else {
        MessageAlert.warning('사용할 수 없는 아이디입니다.');
        setIsChecked('FAIL');
      }
    } catch (err) {
      console.error(`[ACCOUNT CHECK] ERROR : `, err);
      MessageAlert.error(
        '아이디 중복검사를 진행하는데 실패하였습니다. 관리자에게 문의해주세요.',
      );
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    const newData = ShopInformations?.data?.filter(
      (item) => item.application_status === 'APPROVE',
    );
    setData(newData);
  }, [ShopInformations]);

  useEffect(() => {
    if (ShopInformations?.data?.length && defaultShop) {
      const findShop = ShopInformations?.data?.find(
        (shop) => shop?.shop_information_id === defaultShop,
      );

      if (!findShop) return;

      setRegistrationData({
        type: findShop?.type === 'SHOP' ? 'SHOP_ADMIN' : 'STORE_ADMIN',
        shop_information_id: findShop?.shop_information_id,
      });
    }
  }, [defaultShop, ShopInformations?.data]);
  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && (
        <LoadingPage text={'데이터를 처리중입니다. 잠시만 기다려주세요.'} />
      )}
      <ManagerRegisterPresenter
        auth={userInfo?.admin_privilege}
        registrationData={registrationData}
        data={data}
        isChecked={isChecked}
        onSubmit={handleSubmit}
        onChange={handleChange}
        onDuplication={handleDuplication}
        defaultShop={defaultShop}
      />
    </>
  );
};

export default ManagerRegisterContainer;
